<template>
  <div class="global-session">
    <div class="global-session__body">
      <div v-if="isInitialStatus && !isOtherSessionRunning">
        <p class="global-session__text">
          Start the lab and you’ll be able to download a VPN File and connect.
        </p>
      </div>
      <div v-else-if="isRunningStatus">
        <p class="global-session__text">
          Please keep this window open while you’re working on the lab. Labs
          that don't register activity will be automatically stopped.
        </p>
        <p class="global-session__text">
          Download the VPN file and use the provided credentials to login.
        </p>
        <div class="global-session__credential-box">
          <div class="global-session__credential-icons-box">
            Download VPN file
          </div>
          <div class="global-session__credential-icons-box">
            <DownloadIcon
              v-if="!isLoadingDownload"
              class="global-session__credential-icon global-session__credential-icon--pointer"
              @click.prevent="downloadVpnFile()"
              v-tooltip="{
                content: 'Download VPN File',
                placement: 'top-center',
              }"
            />
            <LoadingSmallSpinner v-if="isLoadingDownload" />
          </div>
        </div>
        <div class="global-session__credential-box">
          <div class="global-session__credential-icons-box">
            Username:
          </div>
          <input
            class="global-session__credential-input"
            v-model="currentSession.vpn_username"
            :readonly="true"
          />
          <div class="global-session__credential-icons-box">
            <CopyIcon
              class="global-session__credential-icon global-session__credential-icon--pointer"
              @click="copyToClipboard('username')"
              v-tooltip="{
                content: 'Copy to clipboard',
                placement: 'top-center',
              }"
            />
          </div>
        </div>
        <div class="global-session__credential-box">
          <div class="global-session__credential-icons-box">
            Password:
          </div>
          <input
            class="global-session__credential-input"
            v-model="currentSession.vpn_password"
            type="text"
            :readonly="true"
          />
          <div class="global-session__credential-icons-box">
            <CopyIcon
              class="global-session__credential-icon global-session__credential-icon--pointer"
              @click="copyToClipboard('password')"
              v-tooltip="{
                content: 'Copy to clipboard',
                placement: 'top-center',
              }"
            />
          </div>
        </div>
      </div>
      <div v-else-if="isStoppedStatus && !isOtherSessionRunning">
        <div v-if="!isResetConfirmationOpen">
          <p v-if="isAllowedToResetSession" class="global-session__text">
            Your lab environment has been stopped and its state has been
            preserved.
          </p>
          <p v-else class="global-session__text">
            You’ve exhausted the number of resets available per day.
          </p>
          <button
            class="btn__link global-session__flat-btn"
            @click="toggleResetConfirmation()"
            :disabled="!isAllowedToResetSession"
          >
            Reset Lab
          </button>
        </div>
        <div v-if="isResetConfirmationOpen">
          <p class="global-session__subtitle">
            Are you sure you want to reset your lab?
          </p>
          <p class="global-session__text">
            This action can’t be undone. You can only reset your lab four times
            per day.<br />
            {{ availableResets }} remaining.
          </p>
          <div class="global-session__confirmation-box">
            <button class="btn__link" @click="toggleResetConfirmation()">
              Cancel
            </button>
            <button class="btn__link" @click="resetSession()">
              Reset lab
            </button>
          </div>
        </div>
      </div>
      <div v-if="isOtherSessionRunning" class="global-session__session-running">
        <div class="global-session__session-running__body">
          <p class="global-session__subtitle">
            Another lab is running
          </p>
          <p class="global-session__text">
            You can't start this lab until you stop your other running lab.
          </p>
          <p class="global-session__lab-running">
            Lab running: {{ globalRunningLabName }}
          </p>
          <p class="global-session__lab-running">
            {{ formatRunningTime(runningTime) }}
          </p>
          <button
            class="btn btn--tertiary btn btn--tertiary--with-icon btn btn--tertiary--with-icon--right btn btn--tertiary--small global-session__flat-btn"
            @click="stopGlobalSession()"
          >
            <span class="global-session__flat-btn-text">
              STOP LAB
            </span>
            <StopLabIcon v-if="!isLoadingGlobalSession" />
            <LoadingSmallSpinner v-if="isLoadingGlobalSession" />
          </button>
        </div>
      </div>
      <div class="global-session__error-messages" v-if="customLabError">
        <div class="global-session__error-messages__error">
          <img
            :src="errorImage"
            class="global-session__error-messages__error-img"
            alt="Lab error"
          />
        </div>
        <div class="global-session__error-messages__body">
          <p class="global-session__subtitle">
            Under maintenance
          </p>
          <div class="maintenance-text">
            <p class="global-session__text">{{ customLabError }}</p>
            <p class="global-session__text-support">
              If the issue is not resolved, please email us at
              <a href="mailto:support@ine.com">support@ine.com</a>.
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { DownloadIcon } from 'vue-feather-icons'
import CopyIcon from '@/assets/icons/course/lab/copy-icon.svg'
import LoadingSmallSpinner from '@/components/loaders/LoadingSmallSpinner.vue'
import StopLabIcon from '@/assets/icons/course/lab/stop-lab-icon.svg'
import CONSTANTS from '@/constants'

export default {
  components: {
    CopyIcon: { ...CopyIcon, name: 'CopyIcon' },
    StopLabIcon: { name: 'StopLabIcon', ...StopLabIcon },
    LoadingSmallSpinner,
    DownloadIcon,
  },
  props: {
    currentLab: {
      type: Object,
      required: true,
    },
    currentSession: {
      type: Object,
      required: false,
    },
    customError: {
      type: Number,
      required: false,
      default: () => {
        return null
      },
    },
  },
  data: () => {
    return {
      isLoadingGlobalSession: false,
      isLoadingDownload: false,
      isResetConfirmationOpen: false,
      customLabError: '',
      runningTime: null,
      runningTimeIntervalId: null,
    }
  },
  computed: {
    status() {
      const sessionStatus =
        this.currentSession && this.currentSession.status
          ? this.currentSession.status
          : CONSTANTS.LABS.SESSION_STATUS.IDDLE
      return sessionStatus
    },
    isInitialStatus() {
      return [
        CONSTANTS.LABS.SESSION_STATUS.STARTING,
        CONSTANTS.LABS.SESSION_STATUS.TERMINATING,
        CONSTANTS.LABS.SESSION_STATUS.TERMINATED,
        'error',
        null,
      ].includes(this.status)
    },
    isRunningStatus() {
      return [
        CONSTANTS.LABS.SESSION_STATUS.STARTED,
        CONSTANTS.LABS.SESSION_STATUS.STOPPING,
      ].includes(this.status)
    },
    isStoppedStatus() {
      return [CONSTANTS.LABS.SESSION_STATUS.STOPPED].includes(this.status)
    },
    globalRunningSession() {
      return this.currentLab.global_running_session
    },
    isOtherSessionRunning() {
      const currentSessionId = this.currentSession
        ? this.currentSession.id
        : null
      return (
        this.globalRunningSession &&
        this.globalRunningSession.id !== currentSessionId
      )
    },
    globalRunningLabName() {
      return this.globalRunningSession && this.globalRunningSession.lab.name
    },
    availableResets() {
      return this.currentLab.available_resets
    },
    isAllowedToResetSession() {
      return this.availableResets > 0
    },
    errorImage() {
      return require('@/assets/icons/course/lab/error_status.png')
    },
  },
  watch: {
    customError: {
      inmediate: true,
      handler() {
        this.showCustomError(this.customError)
      },
    },
    isOtherSessionRunning: {
      inmediate: true,
      handler() {
        if (this.isOtherSessionRunning) {
          this.handleRunningTimeInterval()
        }
      },
    },
  },
  methods: {
    formatRunningTime(secs) {
      const sec_num = parseInt(secs, 10)
      const hours = Math.floor(sec_num / 3600)
      const minutes = Math.floor(sec_num / 60) % 60
      const seconds = sec_num % 60
      return [hours, minutes, seconds]
        .map(v => (v < 10 ? `0${v}` : v))
        .join(':')
    },
    increaseRunningTime() {
      this.runningTime += 1
    },
    handleRunningTimeInterval() {
      if (!this.globalRunningSession) return
      this.setInitialRunningTime()
      if (
        this.globalRunningSession.status === 'started' &&
        !this.runningTimeIntervalId
      ) {
        this.runningTimeIntervalId = setInterval(this.increaseRunningTime, 1000)
      } else if (this.globalRunningSession.status !== 'started') {
        this.runningTimeIntervalId = clearInterval(this.runningTimeIntervalId)
      }
    },
    setInitialRunningTime() {
      this.runningTime = this.globalRunningSession.running_time
    },
    async resetSession() {
      this.isLoadingGlobalSession = true
      await this.$store.dispatch('labs/partialUpdateLabSession', {
        labId: this.currentLab.id,
        sessionId: this.currentSession.id,
        status: CONSTANTS.LABS.SESSION_STATUS.TERMINATED,
      })
      await this.$store.dispatch('labs/getLabById', this.currentLab.id)
      this.toggleResetConfirmation()
      this.isLoadingGlobalSession = false
    },
    async stopGlobalSession() {
      this.isLoadingGlobalSession = true
      const statusToStop =
        this.globalRunningSession?.lab.lab_type === 'pta'
          ? CONSTANTS.LABS.SESSION_STATUS.TERMINATED
          : CONSTANTS.LABS.SESSION_STATUS.STOPPED
      await this.$store.dispatch('labs/partialUpdateLabSession', {
        labId: this.globalRunningSession.lab.id,
        sessionId: this.globalRunningSession.id,
        status: statusToStop,
        cleanGlobalSession: true,
      })
      this.isLoadingGlobalSession = false
    },
    async downloadVpnFile() {
      this.isLoadingDownload = true
      const response = await this.$store.dispatch('labs/getLabSessionVPNFile', {
        labId: this.currentLab.id,
        sessionId: this.currentSession.id,
      })
      if (response) {
        const anchor = document.createElement('a')
        anchor.href = window.URL.createObjectURL(
          new Blob([response], { type: 'text/plain' })
        )
        anchor.download = `${this.currentLab.slug}.ovpn`
        anchor.click()
      }
      this.isLoadingDownload = false
    },
    async copyToClipboard(type) {
      if (!type) return
      try {
        await navigator.clipboard.writeText(this.currentSession[`vpn_${type}`])
        this.$store.dispatch(
          'app/setSuccess',
          `VPN ${type} has been copied to your clipboard.`
        )
      } catch (err) {
        this.$store.dispatch(
          'app/setError',
          "Your browser doesn't allow clipboard access."
        )
      }
    },
    toggleResetConfirmation() {
      this.isResetConfirmationOpen = !this.isResetConfirmationOpen
    },
    showCustomError(error) {
      switch (parseInt(error || 0)) {
        case 4001:
          this.customLabError =
            "We're sorry, but this lab is currently under maintenance. Please try another lab or try again in a few minutes."
          break
        case 4002:
          this.customLabError =
            'Our Lab Platform is currently under heavy load and we’re performing some maintenance work, please try again in a few minutes.'
          break
        default:
          this.customLabError = ''
          break
      }
    },
  },
  mounted() {
    if (this.isOtherSessionRunning) {
      this.handleRunningTimeInterval()
    }
    this.showCustomError(this.customError)
  },
  beforeDestroy() {
    this.runningTimeIntervalId = clearInterval(this.runningTimeIntervalId)
  },
}
</script>

<style lang="scss" scoped>
.global-session {
  max-width: 100%;
  width: 100%;
  background: linear-gradient(
    111.02deg,
    rgba(18, 18, 18, 0.7) 3.86%,
    rgba(18, 18, 18, 0) 107.96%
  );
  /* Gray/Gray 4 */

  border: 1px solid var(--tundora-gray);
  box-sizing: border-box;
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  align-self: stretch;
  align-items: flex-start;
  justify-content: center;
  padding: 30px;
  position: relative;
  overflow: hidden;
  @include tablet {
    max-width: 550px;
  }
  &__body {
    text-align: left;
  }
  &__subtitle {
    font-weight: 700;
    display: flex;
    align-items: flex-start;
    margin-bottom: 10px;
    color: var(--white);
    &-icon {
      color: var(--lighter-gray);
      font-size: 26px;
      margin: -3px 5px 0 0;
    }
  }
  &__text {
    &--highlight {
      color: var(--orange);
    }
  }
  &__dark-label {
    background-color: var(--dark-gray);
    font-size: 14px;
    font-weight: 700;
    display: flex;
    justify-content: center;
    border-radius: 15px;
    padding: 5px 15px;
    align-items: center;
    .dark-label-icon {
      min-width: 16px;
      fill: white;
      vertical-align: middle;
      margin-right: 7px;
      max-height: 22px;
    }
  }
  &__credential-box {
    border: 1px solid #77777744;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 4px;
    margin-top: 12px;
  }
  &__credential-input {
    width: 100%;
    color: var(--white);
    background-color: transparent;
    border: none;
    flex-grow: 1;
    overflow-y: auto;
    padding: 0px;
    margin: 0px;
  }
  &__credential-icons-box {
    display: flex;
    white-space: nowrap;
    margin: 4px;
  }
  &__credential-icon {
    fill: transparent;
    stroke: var(--lighter-gray);
    stroke-width: 2px;
    margin: 0 6px;
    &--pointer {
      cursor: pointer;
    }
  }
  &__btn {
    border-radius: 15px;
    padding: 5px 10px;
    font-size: 11px;
    background-color: transparent;
    color: white;
    border: 1px solid var(--orange);
    margin-top: 10px;
    width: fit-content;
    display: flex;
    align-items: center;
    justify-content: center;
    white-space: nowrap;
    text-transform: uppercase;
    cursor: pointer;
    &:disabled {
      background-color: transparent;
      color: var(--flat-black);
      border: 1px solid var(--flat-black);
      cursor: not-allowed;
    }
    &--filled {
      background-color: var(--lighter-gray);
      font-weight: 700;
      border: none;
      margin: 0px 10px 0px 0px;
    }
  }
  &__confirmation-box {
    display: flex;
  }
  &__reset-icon {
    fill: transparent;
    stroke-width: 2px;
    stroke: var(--orange);
  }
  &__error-messages {
    display: flex;
    justify-content: space-between;
    align-content: center;
    align-items: center;
    &__error {
      width: 45%;
    }
    &__error-img {
      width: 100%;
    }
    &__body {
      width: 50%;
      z-index: 1;
      a {
        color: inherit;
        &:hover {
          color: var(--white);
        }
      }
    }
  }
  &__session-running {
    display: flex;
    justify-content: flex-start;
  }
  .btn__link {
    margin-right: 20px;
  }
  &__lab-running {
    color: var(--white);
  }
}
</style>
